import $ from 'jquery';
import zod from 'zod';
import JustValidate from 'just-validate';
import Modal from 'bootstrap/js/src/modal';
import {rIC} from 'idlize/idle-callback-polyfills.mjs';
import ajaxService from '../services/ajaxService';
import {trans} from '../services/translatorService';
import {renderFirstReCaptcha} from './reCaptchaHelpers';

/**
 * @typedef {import('zod').ZodPipeline} ZodPipeline
 * @typedef {import('zod').ZodString}   ZodString
 * @typedef {import('zod').ZodNumber}   ZodNumber
 */

const MIN_PHONE_LENGTH = 6,
	  MAX_PHONE_LENGTH = 15;

class ContactForm {
	/** @type {HTMLFormElement} */
	#form;
	/** @type {JustValidate} */
	#validator;
	
	constructor() {
		this.#form = document.getElementById('contactForm');
		this.#validator = this.#createValidator();
		
		rIC(() => {
			this.#init();
		});
	}
	
	#init() {
		this.#addValidationFields();
		this.#initEvents();
		
		renderFirstReCaptcha({
			'renderParams': {
				'element': document.getElementById('contactFormRecaptcha'),
				'params' : {
					'callback': () => {
						this.#validator.revalidateField('#contactFormRecaptcha');
					}
				}
			}
		});
	}
	
	#createValidator() {
		const validator = new JustValidate(
			this.#form,
			{
				'validateBeforeSubmitting': true,
				'errorFieldCssClass'      : ['is-invalid'],
				'errorLabelCssClass'      : ['invalid-feedback', 'd-block']
			}
		);

		this.#changeInputsListenerType(validator);
		
		return validator;
	}
	
	#addValidationFields() {
		const reasonsListTitle = document.querySelector('.reasonsListTitle').textContent,
		      $reasonSelect   = $('#reasonSelect'),
		      $reasonsList     = $('.reasonsList');
		
		this.#validator
		    .addField(
			    $reasonSelect.get(0),
			    [
				    {
					    'errorMessage': ' ',
					    'validator'   : () => {
						    if ($reasonSelect.is(':hidden')) {
							    return true;
						    }
						    
						    return !!$reasonSelect.val();
					    }
				    }
			    ]
		    )
		    .addField(
			    $reasonsList.get(0),
			    [
				    {
					    'errorMessage': this.#getFormattedErrorMessage(reasonsListTitle),
					    'validator'   : () => {
						    if ($reasonsList.is(':hidden')) {
							    return true;
						    }
						    
						    return !!$reasonsList.find('input:checked').length;
					    }
				    }
			    ]
		    )
		    .addField(
			    '#contactName',
			    [
				    {
					    'rule'        : 'required',
					    'errorMessage': this.#getFormattedErrorMessage(
							trans('required', {'ns': 'errors'})
					    )
				    }
			    ]
		    )
		    .addField(
			    '#contactEmail',
			    [
				    {
					    'validator': (value) => (
						    this.#getEmailSchema().safeParse(value).success
					    ),
					    'errorMessage': this.#getFormattedErrorMessage(
							trans('invalidEmail', {'ns': 'errors'})
					    )
				    }
			    ]
		    )
		    .addField(
			    '#contactPhone',
			    [
				    {
					    'validator': (value) => (
						    this.#getPhoneSchema().safeParse(value).success
					    ),
					    'errorMessage': this.#getFormattedErrorMessage(
							trans('invalidPhone', {'ns': 'errors'})
					    )
				    }
			    ]
		    )
		    .addField(
			    '#contactComment',
			    [
				    {
					    'rule'        : 'required',
					    'errorMessage': this.#getFormattedErrorMessage(
							trans('required', {'ns': 'errors'})
					    )
				    }
			    ]
		    )
		    .addField(
			    '#contactFormRecaptcha',
			    [
				    {
					    'validator': () => (
						    !!window.grecaptcha?.getResponse?.()
					    ),
					    'errorMessage': this.#getFormattedErrorMessage(
							trans('required', {'ns': 'errors'})
					    )
				    }
			    ]
		    );
	}
	
	#initEvents() {
		const reasonList = document.querySelector('.reasonsList');
		
		reasonList.addEventListener(
			'change',
			() => {
				this.#validator.revalidateField(reasonList);
			},
			{
				'once': true
			}
		);
		
		this.#validator.onSuccess(() => {
			if (this.#form.reportValidity()) {
				this.#submitForm();
			}
		});
	}
	
	async #submitForm() {
		const completeFormData = Object.fromEntries(new FormData(this.#form)),
		      {
			      'g-recaptcha-response': reCaptchaToken,
				  ...formData
		      } = completeFormData;

		this.#validator.lockForm();
		
		const result = await ajaxService.sendPost({
			'url' : 'contact',
			'data': {
				formData,
				reCaptchaToken
			}
		});
		
		this.#handleResponse(result);
	}
	
	#handleResponse({response, xhr}) {
		const responseObject = typeof xhr === 'object' ? xhr : response;
		
		let modalSelector = '#contact_form_success_modal';
		
		responseObject
			.catch(() => {
				modalSelector = '#contact_form_error_modal';
				this.#validator.unlockForm();
			})
			.always(() => {
				Modal
					.getOrCreateInstance(modalSelector)
					.show();
			});
	}
	
	/**
	 * @param {string} errorMessage
	 * @returns {string}
	 */
	#getFormattedErrorMessage(errorMessage) {
		return `<i class="eventTripsIcon-warning"></i> ${errorMessage}`;
	}
	
	/**
	 * @returns {ZodString}
	 */
	#getEmailSchema() {
		return zod.string().email();
	}
	
	/**
	 * @returns {ZodPipeline<ZodString, ZodNumber>}
	 */
	#getPhoneSchema() {
		const lengthSchema = zod.string().min(MIN_PHONE_LENGTH).max(MAX_PHONE_LENGTH),
		      numberSchema = zod.coerce.number().positive().int();
		
		return lengthSchema.pipe(numberSchema);
	}
	
	/**
	 * Magic method that changes the type of event listener for the input fields so change event will trigger validation
	 * @param {JustValidate} validator
	 */
	#changeInputsListenerType(validator) {
		const oldGetListenerType = validator.getListenerType;
		
		validator.getListenerType = (type) => {
			if (type === 'text') {
				return 'change';
			}
			
			return oldGetListenerType(type);
		};
	}
}

export default ContactForm;